import React from 'react';
import { cn } from '../../lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  containerClassName?: string;
}

const fieldBase =
  'h-11 w-full rounded-lg border bg-surface-1 px-3 text-base text-foreground placeholder:text-foreground-subtle ' +
  'transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftIcon, className, containerClassName, id, ...props }, ref) => {
    const inputId = id || props.name;
    return (
      <div className={cn('flex flex-col gap-1.5', containerClassName)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-subtle">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              fieldBase,
              leftIcon ? 'pl-10' : null,
              error ? 'border-danger focus-visible:ring-danger/30' : 'border-border hover:border-border-strong',
              className
            )}
            aria-invalid={!!error}
            {...props}
          />
        </div>
        {error ? (
          <span className="text-sm text-danger">{error}</span>
        ) : hint ? (
          <span className="text-sm text-foreground-muted">{hint}</span>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
